/**
 * Pinned QuickHub release public keys (ES256 / P-256). The installer verifies the image
 * manifest signature against one of these BEFORE parsing the manifest, so a compromised
 * CDN or cloud cannot feed the page an image it will flash.
 *
 * ROTATION — how a new release key goes live:
 *   1. Key ceremony produces the new key pair on the offline signing host
 *      (see os/docs/key-ceremony.md); only the public JWK leaves that host.
 *   2. Add the new JWK here with a new `kid` and `valid_from`; keep the old key until every
 *      published manifest signed with it has been superseded (manifests carry `key_id`).
 *   3. Ship the installer (this page + the CLI, which pins the same set in
 *      installer/cli/qh_install/release_keys.py — keep both files identical).
 *   4. Start signing manifests with the new key; then retire the old kid in a later release.
 *   Compromise: remove the kid immediately and ship; manifests signed with it stop verifying.
 *
 * The key below is the DEVELOPMENT key (private half in installer/web/dev/, NOT secret).
 * Production keys are added at the key ceremony; never sign a customer-facing manifest with
 * the dev key.
 */
export interface ReleaseKey {
  kid: string;
  jwk: JsonWebKey;
  /** ISO date; informational (manifests before this date must not use the key). */
  valid_from: string;
  environment: "development" | "production";
}

export const RELEASE_KEYS: readonly ReleaseKey[] = [
  {
    kid: "qh-release-dev-2026",
    environment: "development",
    valid_from: "2026-09-25",
    jwk: {
      kty: "EC",
      crv: "P-256",
      x: "1aWsqhbkWnUXbgaFjyR8YwyLH-RIu-MYkr0g6miEhCI",
      y: "pqmqx4naQ9oZhmTgXYfhdhJv_bbij2NHdZWXFQ0kbPQ",
    },
  },
];

/**
 * The keys a session may verify manifests with. Development keys (private half in installer/web/dev/, NOT secret)
 * are trusted only on explicit opt-in — the Vite dev server, or a build with VITE_QH_ALLOW_DEV_KEYS=true for the lab
 * — never in a normal production build (#10122: every installer trusted the dev key with no environment filter).
 */
export function trustedKeys(allowDev: boolean, keys: readonly ReleaseKey[] = RELEASE_KEYS): readonly ReleaseKey[] {
  return keys.filter((k) => allowDev || k.environment === "production");
}
